import { Button, styled } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Control, Controller, UseFormGetValues, UseFormSetValue, UseFormTrigger, RegisterOptions, FieldPath } from "react-hook-form";
import { useKeyPress } from "../../../../../hooks/useKeyPress";
import colors from "../../../../../theme/themeConstants";
import CustomInput from "../../../CustomInput";
import { BaseButton } from "components/buttons/BaseButton";
import { ISignUpFieldValues } from "../AuthSignUp";
import { apiEmail } from "../../../../../services";

interface IProps {
  next: () => void;
  back: () => void;
  control: Control<ISignUpFieldValues>;
  trigger: UseFormTrigger<ISignUpFieldValues>;
  setValue: UseFormSetValue<ISignUpFieldValues>;
  getValues: UseFormGetValues<ISignUpFieldValues>;
}

const CODE_LENGTH = 6;

const SignUpCodeStep = (props: IProps) => {
  const { next, back, control, trigger, setValue, getValues } = props;

  const [disabled, setDisabled] = useState(false);

  const enterPressed = useKeyPress("Enter");

  const handleNext = useCallback(async () => {
    if (await trigger("validation_code")) {
      next();
    }
  }, [next, trigger]);

  const handleResendCode = () => {
    back();
  };

  useEffect(() => {
    if (enterPressed) {
      handleNext();
    }
  }, [enterPressed, handleNext]);

  return (
    <Container>
      <p>Enter 6-digits code:</p>
      <Controller
        name="validation_code"
        control={control}
        defaultValue=""
        rules={{
          required: {
            value: true,
            message: "Required Field!"
          },
          validate: {
            value: async (value: string) => {
              if (value.length === CODE_LENGTH) {
                setDisabled(true);

                try {
                  await apiEmail.checkCode({
                    token: getValues().validation_token,
                    code: value
                  });
                  setValue<FieldPath<ISignUpFieldValues>>("is_confirm_email", true);
                  next();
                } catch (e: any) {
                  console.warn(e);
                  setValue<FieldPath<ISignUpFieldValues>>("is_confirm_email", false);
                  return e.response?.data.error.message;
                } finally {
                  setDisabled(false);
                }
              }

              return false;
            }
          }
        } as RegisterOptions }
        render={({ field, fieldState }) => (
          <Block>
            <label>Your code was sent to {getValues().email}</label>
            <CustomInput
              value={field.value}
              onChange={async (e) => {
                field.onChange(e);
                await trigger("validation_code");
              }}
              error={!!fieldState.error}
              helperText={fieldState.error ? fieldState.error.message : ""}
              fullWidth
            />
            <Button
              type="button"
              sx={{
                width: "fit-content",
                alignSelf: "flex-end",
                fontWeight: 500,
                fontSize: "12px",
                color: colors.textDark,
                fontFamily: "Poppins"
              }}
              onClick={handleResendCode}
            >
              Resend Code
            </Button>
          </Block>
        )}
      />
      <StyledBaseButton>
        <BaseButton type="button" onClick={handleNext} disabled={disabled}>Next</BaseButton>
      </StyledBaseButton>
    </Container>
  );
};

export default SignUpCodeStep;

const Container = styled("div")({
  marginTop: "2rem",
  display: "flex",
  flexDirection: "column",
  gap: "1rem"
});

const Block = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
  "& label": {
    fontSize: "12px",
    color: colors.textDark
  }
});

const StyledBaseButton = styled("div")({
  ".tablet &, .phone &": {
    position: "absolute",
    marginTop: 0,
    bottom: "0",
    left: "0",
    width: "100%",
    padding: "16px 16px 32px",
    background: "rgba(255, 255, 255, 0.9)",
    borderRadius: "24px"
  }
});
